<template>
  <div class="card">
    <div class="card-header">
      <div class="row align-items-center">
        <div class="col-md-6">
          <div class="card-title">Painel de Agendamentos</div>
        </div>
        <div class="col-md-6 text-right">
          <div class="btn-group" role="group">
            <button type="button" class="btn btn-sm"
              :class="viewMode === 'weekly' ? 'btn-primary' : 'btn-outline-primary'" @click="setViewMode('weekly')">
              <i class="fas fa-calendar-week"></i> Semanal
            </button>
            <button type="button" class="btn btn-sm"
              :class="viewMode === 'monthly' ? 'btn-primary' : 'btn-outline-primary'" @click="setViewMode('monthly')">
              <i class="fas fa-calendar-alt"></i> Mensal
            </button>
          </div>
        </div>
      </div>
    </div>
    <div class="card-body">
      <!-- Filtros -->
      <div class="row mb-3">
        <div class="col-md-3">
          <label for="filterCompany" class="form-label">Empresa</label>
          <select v-model="filters.company_id" class="form-control form-control-sm" id="filterCompany">
            <option value="">Todas as empresas</option>
            <option v-for="company in companies" :value="company.id" :key="company.id">
              {{ company.company_name_fantasy }}
            </option>
          </select>
        </div>
        <div class="col-md-2">
          <label for="filterRoom" class="form-label">Sala</label>
          <select v-model="filters.room_id" class="form-control form-control-sm" id="filterRoom">
            <option value="">Todas as salas</option>
            <option v-for="room in rooms" :value="room.id" :key="room.id">
              {{ room.name }}
            </option>
          </select>
        </div>
        <div class="col-md-2">
          <label for="filterBranch" class="form-label">Unidade</label>
          <select v-model="filters.branch_id" class="form-control form-control-sm" id="filterBranch">
            <option value="">Todas as unidades</option>
            <option v-for="branch in branches" :value="branch.id" :key="branch.id">
              {{ branch.branch_name }}
            </option>
          </select>
        </div>
        <div class="col-md-2">
          <label for="filterDate" class="form-label">
            {{ viewMode === 'weekly' ? 'Data Inicial' : 'Mês/Ano' }}
          </label>
          <input :type="viewMode === 'weekly' ? 'date' : 'month'" v-model="filters.date_input"
            class="form-control form-control-sm" id="filterDate" @change="updateDates" />
        </div>
        <div class="col-md-2 d-flex align-items-end">
          <button @click="applyFilters" class="btn btn-primary btn-sm">
            <i class="fas fa-filter"></i> Filtrar
          </button>
        </div>
      </div>

      <!-- Legenda -->
      <div class="row mb-3">
        <div class="col-md-12">
          <div class="legend-container">
            <span class="legend-title">Legenda:</span>
            <span class="legend-item">
              <span class="legend-color legend-red"></span>
              Com serviços adicionais
            </span>
            <span class="legend-item">
              <span class="legend-color legend-blue"></span>
              Sem serviços adicionais
            </span>
          </div>
        </div>
      </div>

      <div class="table-responsive">

        <table class="table table-bordered table-hover">
          <thead class="thead-dark">
            <tr>
              <th rowspan="1" class="room-header">Sala</th>
              <th rowspan="1" class="time-header">Horário</th>
              <th v-for="date in dates" :key="date" class="date-header">{{ formatDate(date) }}</th>
            </tr>
          </thead>
          <tbody>
            <template v-for="(room, roomIndex) in filteredRooms">
              <tr v-for="(timeSlot, timeIndex) in timeSlots" :key="`${room.id}-${timeSlot}`"
                :class="{ 'room-separator': timeIndex === timeSlots.length - 1 && roomIndex < filteredRooms.length - 1 }">
                <td v-if="timeIndex === 0" :rowspan="timeSlots.length" class="room-name">{{ room.name }}</td>
                <td class="time-slot">{{ timeSlot }}</td>
                <td v-for="date in dates" :key="`${room.id}-${timeSlot}-${date}`" class="booking-cell">
                  <span
                    :class="[getBookingClass(room.id, timeSlot, date), 'booking-status', { 'clickable': hasBooking(room.id, timeSlot, date) }]"
                    :title="getFullUserName(room.id, timeSlot, date)"
                    @click="openBookingModal(room.id, timeSlot, date)">
                    {{ getBookingStatus(room.id, timeSlot, date) }}
                  </span>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Modal de Detalhes da Reserva -->
    <b-modal
      id="bookingModal"
      v-model="isModalOpen"
      size="xl"
      centered
      hide-footer
      content-class="booking-modal"
      header-class="booking-modal-header"
      body-class="booking-modal-body"
      @hidden="onBookingModalHidden"
    >
      <template #modal-header="{ close }">
        <div class="header-content">
          <div class="header-icon">
            <i class="fas fa-calendar-check"></i>
          </div>
          <div class="header-text">
            <h5 class="modal-title text-white" id="bookingModalLabel">Detalhes da Reserva</h5>
            <p class="modal-subtitle text-white" v-if="selectedBooking">
              {{ selectedBooking.title || 'Reunião sem título' }} - {{ formatDate(selectedBooking.date) }} - {{
            selectedBooking.is_online ? 'Online' : 'Presencial' }}
            </p>
          </div>
        </div>
        <button type="button" class="close" aria-label="Close" @click="close()">
          <span aria-hidden="true">&times;</span>
        </button>
      </template>

      <div v-if="selectedBooking">
        <!-- Informações Principais -->
        <div class="booking-info-section">
          <div class="info-grid">
            <div class="info-card">
              <div class="info-icon">
                <i class="fas fa-user"></i>
              </div>
              <div class="info-content">
                <h6>Solicitante</h6>
                <p>{{ selectedBooking.user_name }}</p>
              </div>
            </div>
            <div class="info-card">
              <div class="info-icon">
                <i class="fas fa-door-open"></i>
              </div>
              <div class="info-content">
                <h6>Sala</h6>
                <p>{{ selectedBooking.room_name }}</p>
              </div>
            </div>
            <div class="info-card">
              <div class="info-icon">
                <i class="fas fa-calendar-day"></i>
              </div>
              <div class="info-content">
                <h6>Data</h6>
                <p>{{ formatDate(selectedBooking.date) }}</p>
              </div>
            </div>
            <div class="info-card">
              <div class="info-icon">
                <i class="fas fa-clock"></i>
              </div>
              <div class="info-content">
                <h6>Horário</h6>
                <p>{{ getBookingTimeRange(selectedBooking) }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Descrição -->
        <div class="description-section" v-if="selectedBooking.description">
          <h6><i class="fas fa-align-left"></i> Descrição</h6>
          <div class="description-content">
            {{ selectedBooking.description }}
          </div>
        </div>

        <!-- Participantes -->
        <div class="guests-section" v-if="selectedBooking.participants && selectedBooking.participants.length > 0">
          <h6><i class="fas fa-users"></i> Participantes ({{ selectedBooking.participants.length }})</h6>
          <div class="guests-grid">
            <div v-for="participant in selectedBooking.participants" :key="participant.id" class="guest-card">
              <div class="guest-avatar">
                <i class="fas fa-user"></i>
              </div>
              <div class="guest-info">
                <h6>{{ participant.name }}</h6>
                <p v-if="participant.email">{{ participant.email }}</p>
                <!-- <span v-if="participant.status" class="guest-status" :class="getGuestStatusClass(participant.status)">
                  {{ getGuestStatusText(participant.status) }}
                </span> -->
                <span v-if="participant.select_remote" class="remote-badge">
                  <i class="fas fa-video"></i> Remoto
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Serviços Adicionais -->
        <div class="services-section">
          <h6><i class="fas fa-concierge-bell"></i> Serviços Adicionais</h6>
          <div v-if="selectedBooking.additional_services && selectedBooking.additional_services.length > 0">
            <div class="services-grid">
              <div v-for="service in selectedBooking.additional_services" :key="service.id" class="service-card">
                <div class="service-header text-center">
                  <li class="list-unstyled">{{ service.additional ? service.additional.title : 'Serviço' }}</li>
                </div>
              </div>
            </div>
          </div>
          <div v-else class="no-services">
            <div class="no-services-icon">
              <i class="fas fa-concierge-bell"></i>
            </div>
            <p>Nenhum serviço adicional solicitado</p>
          </div>
        </div>
      </div>

     
    </b-modal>
  </div>
</template>

<script>
export default {
  name: 'SchedulePanel',
  props: {
    // Dados dos agendamentos
    bookings: {
      type: Array,
      default: () => []
    },
    // Salas disponíveis
    rooms: {
      type: Array,
      default: () => []
    },
    // Empresas disponíveis
    companies: {
      type: Array,
      default: () => []
    },
    // Unidades disponíveis
    branches: {
      type: Array,
      default: () => []
    },
    // Horários disponíveis (padrão: 07:00 às 20:00, de 30 em 30)
    timeSlots: {
      type: Array,
      default: () => {
        const slots = [];
        for (let hour = 7; hour <= 20; hour++) {
          slots.push(`${hour.toString().padStart(2, '0')}:00`);
          if (hour < 20) {
            slots.push(`${hour.toString().padStart(2, '0')}:30`);
          }
        }
        return slots;
      }
    }
  },
  data() {
    return {
      viewMode: 'weekly', // 'weekly' ou 'monthly'
      filters: {
        company_id: '',
        room_id: '',
        branch_id: '',
        date_input: ''
      },
      dates: [],
      filteredBookings: [],
      filteredRooms: [],
      selectedBooking: null,
      isModalOpen: false
    }
  },
  methods: {
    formatDate(dateString) {
      const date = new Date(dateString + 'T00:00:00-03:00');
      return date.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: '2-digit'
      });
    },
    addMinutesToSlot(timeSlot, minutesToAdd) {
      if (!timeSlot) {
        return '';
      }
      const [hours, minutes] = timeSlot.split(':').map(Number);
      const totalMinutes = (hours * 60) + minutes + minutesToAdd;
      const normalizedMinutes = totalMinutes % (24 * 60);
      const nextHours = Math.floor(normalizedMinutes / 60);
      const nextMinutes = normalizedMinutes % 60;
      return `${String(nextHours).padStart(2, '0')}:${String(nextMinutes).padStart(2, '0')}`;
    },
    getBookingTimeRange(booking) {
      if (!booking) {
        return '';
      }
      const reservationId = booking.reservation_id || booking.id;
      const slots = this.filteredBookings
        .filter(item => String(item.reservation_id || item.id) === String(reservationId) && item.date === booking.date)
        .map(item => item.time_slot)
        .sort();

      if (slots.length === 0) {
        return booking.time_slot || '';
      }

      const start = slots[0];
      const end = this.addMinutesToSlot(slots[slots.length - 1], 30);
      return start === end ? start : `${start} - ${end}`;
    },
    getBookingStatus(roomId, timeSlot, date) {
      const booking = this.filteredBookings.find(b =>
        String(b.room_id) === String(roomId) &&
        b.time_slot === timeSlot &&
        b.date === date
      );

      if (booking) {
        // Truncar nome se for muito longo
        const name = booking.user_name || 'Usuário';
        return name.split(' ')[0];
      }

      return '';
    },

    getFullUserName(roomId, timeSlot, date) {
      const booking = this.filteredBookings.find(b =>
        String(b.room_id) === String(roomId) &&
        b.time_slot === timeSlot &&
        b.date === date
      );

      if (booking) {
        return booking.user_name || 'Usuário';
      }

      return 'Livre';
    },

    getBookingClass(roomId, timeSlot, date) {
      const booking = this.filteredBookings.find(b =>
        String(b.room_id) === String(roomId) &&
        b.time_slot === timeSlot &&
        b.date === date
      );

      if (booking) {
        // Verifica se tem serviços adicionais
        const hasAdditionalServices = booking.additional_services &&
          Array.isArray(booking.additional_services) &&
          booking.additional_services.length > 0;
        return hasAdditionalServices ? 'booked-with-services' : 'booked-without-services';
      }

      return 'available';
    },

    hasBooking(roomId, timeSlot, date) {
      return this.filteredBookings.some(b =>
        String(b.room_id) === String(roomId) &&
        b.time_slot === timeSlot &&
        b.date === date
      );
    },

    openBookingModal(roomId, timeSlot, date) {
      const booking = this.filteredBookings.find(b =>
        String(b.room_id) === String(roomId) &&
        b.time_slot === timeSlot &&
        b.date === date
      );

      if (booking) {
        // Usar dados disponíveis no booking
        this.selectedBooking = {
          id: booking.id || booking.reservation_id || 'N/A',
          title: booking.title || 'Reunião sem título',
          description: booking.description || '',
          user_name: booking.user_name || 'Usuário',
          room_name: booking.room_name || 'Sala',
          date: booking.date || '',
          time_slot: booking.time_slot || '',
          is_online: Boolean(booking.is_online),
          additional_services: booking.additional_services || [],
          participants: booking.participants || []
        };
        this.isModalOpen = true;
      }
    },
    onBookingModalHidden() {
      this.isModalOpen = false;
      this.selectedBooking = null;
    },


    getServiceStatusClass(status) {
      const statusClasses = {
        0: 'badge-warning',
        1: 'badge-primary',
        2: 'badge-success'
      };
      return statusClasses[status] || 'badge-secondary';
    },

    getServiceStatusText(status) {
      const statusTexts = {
        0: 'Aguardando',
        1: 'Em andamento',
        2: 'Finalizado'
      };
      return statusTexts[status] || 'Desconhecido';
    },

    getGuestStatusClass(status) {
      const statusClasses = {
        'confirmed': 'guest-confirmed',
        'pending': 'guest-pending',
        'declined': 'guest-declined'
      };
      return statusClasses[status] || 'guest-unknown';
    },

    getGuestStatusText(status) {
      const statusTexts = {
        'confirmed': 'Confirmado',
        'pending': 'Pendente',
        'declined': 'Recusou'
      };
      return statusTexts[status] || 'Desconhecido';
    },

    setViewMode(mode) {
      this.viewMode = mode;
      this.updateDates();
    },

    // Métodos para filtros
    updateDates() {
      if (this.filters.date_input) {
        this.dates = [];

        if (this.viewMode === 'weekly') {
          // Visão semanal: 7 dias a partir da data selecionada
          const startDate = new Date(this.filters.date_input);
          for (let i = 0; i < 7; i++) {
            const date = new Date(startDate);
            date.setDate(startDate.getDate() + i);
            this.dates.push(date.toISOString().split('T')[0]);
          }
        } else {
          // Visão mensal: todos os dias do mês selecionado
          const [year, month] = this.filters.date_input.split('-');
          let startDate = new Date(year, month - 1, 1);
          let endDate = new Date(year, month, 1); // Último dia do mês
          endDate.setDate(endDate.getDate() - 1);

          const currentDate = new Date(startDate);
          while (currentDate <= endDate) {
            this.dates.push(currentDate.toISOString().split('T')[0]);
            currentDate.setDate(currentDate.getDate() + 1);
          }
        }
      } else {
        // Se não há data selecionada, usar padrão baseado na visualização
        this.dates = [];
        const today = new Date();

        if (this.viewMode === 'weekly') {
          // Semanal: próximos 7 dias
          for (let i = 0; i < 7; i++) {
            const date = new Date(today);
            date.setDate(today.getDate() + i);
            this.dates.push(date.toISOString().split('T')[0]);
          }
        } else {
          // Mensal: mês atual
          const currentMonth = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0');
          this.filters.date_input = currentMonth;
          this.updateDates();
          return;
        }
      }

      this.applyFilters();
    },

    applyFilters() {
      // Filtrar salas
      this.filteredRooms = this.rooms.filter(room => {
        if (this.filters.room_id && room.id != this.filters.room_id) {
          return false;
        }
        return true;
      });

      // Filtrar agendamentos
      this.filteredBookings = this.bookings.filter(booking => {
        // Filtrar por sala
        if (this.filters.room_id && booking.room_id != this.filters.room_id) {
          return false;
        }

        // Filtrar por data (se há datas selecionadas)
        if (this.dates.length > 0 && !this.dates.includes(booking.date)) {
          return false;
        }

        return true;
      });

      // Emitir evento para o componente pai buscar novos dados se necessário
      this.$emit('filters-changed', this.filters);
    },

    initializeFilters() {
      // Inicializar com a data atual baseada na visualização
      const today = new Date();

      if (this.viewMode === 'weekly') {
        // Semanal: data de hoje
        this.filters.date_input = today.toISOString().split('T')[0];
      } else {
        // Mensal: mês atual
        const currentMonth = today.getFullYear() + '-' + String(today.getMonth() + 1).padStart(2, '0');
        this.filters.date_input = currentMonth;
      }

      this.updateDates();
    }
  },

  watch: {
    bookings: {
      handler(newBookings) {
        this.filteredBookings = [...newBookings];
        // Não chamar applyFilters aqui para evitar loop infinito
      },
      immediate: true
    },
    rooms: {
      handler(newRooms) {
        this.filteredRooms = [...newRooms];
        // Não chamar applyFilters aqui para evitar loop infinito
      },
      immediate: true
    }
  },

  mounted() {
    this.initializeFilters();
  }
}
</script>

<style scoped>
.table {
  margin-bottom: 0;
  font-size: 0.9em;
}

.room-header,
.time-header,
.date-header {
  text-align: center;
  vertical-align: middle;
  font-weight: 600;
  background-color: #343a40 !important;
  color: white !important;
  border-color: #454d55 !important;
  width: 100px;
}

.room-header {
  min-width: 100px;
  max-width: 100px;
}

.time-header {
  min-width: 70px;
  max-width: 90px;
}

.date-header {
  min-width: 60px;
  max-width: 60px;
  font-size: 0.8em;
}

.room-name {
  font-weight: bold;
  background-color: #f8f9fa;
  vertical-align: middle;
  text-align: center;
  min-width: 100px;
  max-width: 100px;
  border-right: 3px solid #007bff;
  border-bottom: 2px solid #007bff;
  font-size: 0.85em;
}

.time-slot {
  font-weight: 600;
  background-color: #e9ecef;
  text-align: center;
  padding: 6px 2px;
  min-width: 70px;
  max-width: 70px;
  border-right: 1px solid #dee2e6;
  font-size: 0.8em;
}

.booking-cell {
  padding: 2px 1px;
  text-align: center;
  min-width: 60px;
  max-width: 60px;
  height: 35px;
  vertical-align: middle;
  border-right: 1px solid #dee2e6;
  overflow: hidden;
  word-wrap: break-word;
}

.booking-status {
  display: inline-block;
  padding: 2px 4px;
  border-radius: 3px;
  font-size: 0.8em;
  font-weight: 500;
  max-width: 55px;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
}

.booking-status.available {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.booking-status.booked-with-services {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

.booking-status.booked-without-services {
  background-color: #d1ecf1;
  color: #0c5460;
  border: 1px solid #bee5eb;
}

.booking-status.clickable {
  cursor: pointer;
  transition: all 0.2s ease;
}

.booking-status.clickable:hover {
  transform: scale(1.05);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
}

/* Separador entre salas */
.room-separator td {
  border-bottom: 3px solid #007bff !important;
}

.room-separator .time-slot {
  border-bottom: 3px solid #007bff !important;
}

.room-separator .booking-cell {
  border-bottom: 3px solid #007bff !important;
}

.table-responsive {
  max-height: 700px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
}

.card-title {
  margin-bottom: 0;
  font-weight: 600;
  color: #495057;
}

.card {
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  border: 1px solid rgba(0, 0, 0, 0.125);
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid rgba(0, 0, 0, 0.125);
}

/* Hover effects */
.table tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.booking-status:hover {
  transform: scale(1.05);
  transition: transform 0.2s ease;
}

/* Responsividade */
@media (max-width: 768px) {
  .table-responsive {
    font-size: 0.75em;
  }

  .booking-cell {
    min-width: 80px;
    padding: 4px 2px;
  }

  .booking-status {
    font-size: 0.7em;
    padding: 4px 6px;
    min-width: 50px;
  }

  .room-name {
    min-width: 100px;
  }

  .time-slot {
    min-width: 70px;
    padding: 6px 2px;
  }
}

@media (max-width: 576px) {
  .table-responsive {
    font-size: 0.7em;
  }

  .booking-status {
    font-size: 0.65em;
    padding: 3px 4px;
    min-width: 45px;
  }
}

/* Legenda */
.legend-container {
  background-color: #f8f9fa;
  padding: 10px 15px;
  border-radius: 5px;
  border: 1px solid #dee2e6;
}

.legend-title {
  font-weight: 600;
  margin-right: 15px;
  color: #495057;
}

.legend-item {
  margin-right: 20px;
  display: inline-flex;
  align-items: center;
  font-size: 0.9em;
}

.legend-color {
  width: 16px;
  height: 16px;
  border-radius: 3px;
  margin-right: 8px;
  display: inline-block;
  border: 1px solid rgba(0, 0, 0, 0.2);
}

.legend-red {
  background-color: #f8d7da;
  border-color: #f5c6cb;
}

.legend-blue {
  background-color: #d1ecf1;
  border-color: #bee5eb;
}

/* Modal Melhorado */
.booking-modal {
  border: none;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  overflow: hidden;
}

.booking-modal-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  border: none;
  padding: 15px 20px;
  position: relative;
}

.booking-modal-header::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100"><defs><pattern id="grain" width="100" height="100" patternUnits="userSpaceOnUse"><circle cx="25" cy="25" r="1" fill="white" opacity="0.1"/><circle cx="75" cy="75" r="1" fill="white" opacity="0.1"/><circle cx="50" cy="10" r="0.5" fill="white" opacity="0.1"/></pattern></defs><rect width="100" height="100" fill="url(%23grain)"/></svg>');
  opacity: 0.3;
}

.header-content {
  display: flex;
  align-items: center;
  position: relative;
  z-index: 1;
}

.header-icon {
  font-size: 1.5rem;
  margin-right: 12px;
  opacity: 0.9;
}

.header-text {
  flex: 1;
}

.modal-title {
  font-size: 1.3rem;
  font-weight: 700;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.modal-subtitle {
  font-size: 0.85rem;
  margin: 3px 0 0 0;
  opacity: 0.9;
  font-weight: 400;
}

.booking-modal-body {
  padding: 20px;
  background-color: #f8f9fa;
}

.booking-info-section {
  margin-bottom: 20px;
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.info-card {
  background: white;
  padding: 12px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.07);
  border: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  transition: transform 0.2s ease, box-shadow 0.2s ease;
}

.info-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 15px rgba(0, 0, 0, 0.1);
}

.info-icon {
  width: 35px;
  height: 35px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  color: white;
  font-size: 0.9rem;
}

.info-content h6 {
  margin: 0 0 3px 0;
  color: #6c757d;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.info-content p {
  margin: 0;
  color: #495057;
  font-size: 0.9rem;
  font-weight: 600;
}

.description-section,
.guests-section,
.services-section {
  background: white;
  padding: 15px;
  border-radius: 8px;
  margin-bottom: 15px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.07);
  border: 1px solid #e9ecef;
}

.description-section h6,
.guests-section h6,
.services-section h6 {
  color: #495057;
  font-weight: 700;
  margin-bottom: 12px;
  font-size: 0.95rem;
  display: flex;
  align-items: center;
}

.description-section h6 i,
.guests-section h6 i,
.services-section h6 i {
  margin-right: 8px;
  color: #667eea;
  font-size: 0.9rem;
}

.description-content {
  background: #f8f9fa;
  padding: 10px;
  border-radius: 6px;
  border-left: 3px solid #667eea;
  font-style: italic;
  color: #495057;
  line-height: 1.4;
  font-size: 0.9rem;
}

.guests-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 10px;
}

.guest-card {
  background: #f8f9fa;
  padding: 10px;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  transition: all 0.2s ease;
}

.guest-card:hover {
  background: white;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-1px);
}

.guest-avatar {
  width: 30px;
  height: 30px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  color: white;
  font-size: 0.8rem;
}

.guest-info h6 {
  margin: 0 0 3px 0;
  color: #495057;
  font-size: 0.85rem;
  font-weight: 600;
}

.guest-info p {
  margin: 0 0 3px 0;
  color: #6c757d;
  font-size: 0.75rem;
}

.guest-status {
  font-size: 0.65rem;
  padding: 2px 6px;
  border-radius: 8px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.guest-confirmed {
  background-color: #d4edda;
  color: #155724;
}

.guest-pending {
  background-color: #fff3cd;
  color: #856404;
}

.guest-declined {
  background-color: #f8d7da;
  color: #721c24;
}

.remote-badge {
  font-size: 0.65rem;
  padding: 2px 6px;
  border-radius: 8px;
  background-color: #e3f2fd;
  color: #1976d2;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  display: inline-flex;
  align-items: center;
  margin-top: 3px;
}

.remote-badge i {
  margin-right: 3px;
  font-size: 0.6rem;
}

.services-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 12px;
}

.service-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 12px;
  border-radius: 8px;
  border: 1px solid #dee2e6;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
}

.service-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.service-card:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.service-header {
  display: flex;
  align-items: flex-start;
}

.service-icon {
  width: 30px;
  height: 30px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  color: white;
  font-size: 0.9rem;
}

.service-title {
  flex: 1;
}

.service-title h6 {
  margin: 0 0 5px 0;
  color: #495057;
  font-size: 0.9rem;
  font-weight: 700;
}

.service-description {
  color: #6c757d;
  font-size: 0.8rem;
  line-height: 1.4;
  margin-bottom: 8px;
}

.service-notes {
  background: white;
  padding: 8px;
  border-radius: 4px;
  border-left: 2px solid #667eea;
  font-size: 0.8rem;
  color: #495057;
  margin-top: 8px;
}

.service-email,
.service-interaction {
  background: white;
  padding: 6px 8px;
  border-radius: 4px;
  border-left: 2px solid #28a745;
  font-size: 0.75rem;
  color: #495057;
  margin-top: 6px;
}

.service-email {
  border-left-color: #17a2b8;
}

.service-interaction {
  border-left-color: #ffc107;
}

.no-services {
  text-align: center;
  padding: 25px 15px;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-radius: 8px;
  border: 2px dashed #dee2e6;
}

.no-services-icon {
  font-size: 2rem;
  color: #adb5bd;
  margin-bottom: 10px;
}

.no-services p {
  color: #6c757d;
  font-size: 0.9rem;
  margin: 0;
  font-weight: 500;
}

.booking-modal-footer {
  background: #f8f9fa;
  border: none;
  padding: 15px 20px;
  border-radius: 0 0 15px 15px;
}

.booking-modal-footer .btn {
  padding: 8px 20px;
  border-radius: 6px;
  font-weight: 600;
  font-size: 0.9rem;
  transition: all 0.2s ease;
}

.booking-modal-footer .btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}
</style>



// WEBPACK FOOTER //
// src/components/Utils/SchedulePanel.vue